/**
 * Глобальный сканер data-pl-* enhancers.
 *
 * При первой загрузке проходит по DOM, навешивает поведение на найденные
 * элементы. Затем подписывается на MutationObserver на `<body>` чтобы
 * обрабатывать динамически добавленные элементы (важно для AI-редактора,
 * который перерисовывает секции).
 *
 * Каждый init-функция идемпотентна (dataset-флаг), повторный сканер не
 * дублирует handlers.
 */

import { initLightbox } from './lightbox.js';
import { initAnchor } from './anchor.js';
import { initReveal } from './reveal.js';
import { initCopy } from './copy.js';
import { initCommerce } from '../commerce/runtime.js';

const SELECTORS = {
  lightbox: '[data-pl-lightbox], [data-pl-lightbox-group]',
  anchor: '[data-pl-anchor]',
  reveal: '[data-pl-reveal]',
  copy: '[data-pl-copy], [data-pl-copy-from]',
} as const;

function scanRoot(root: ParentNode): void {
  initLightbox(root.querySelectorAll(SELECTORS.lightbox));
  initAnchor(root.querySelectorAll(SELECTORS.anchor));
  initReveal(root.querySelectorAll(SELECTORS.reveal));
  initCopy(root.querySelectorAll(SELECTORS.copy));
  initCommerce(root);

  // root сам тоже может матчиться (для динамически добавленного элемента).
  if (root instanceof Element) {
    if (root.matches(SELECTORS.lightbox)) initLightbox([root]);
    if (root.matches(SELECTORS.anchor)) initAnchor([root]);
    if (root.matches(SELECTORS.reveal)) initReveal([root]);
    if (root.matches(SELECTORS.copy)) initCopy([root]);
  }
}

let mutationTimer: number | undefined;
const pending: Element[] = [];

function flushMutations(): void {
  mutationTimer = undefined;
  const nodes = pending.splice(0, pending.length);
  for (const n of nodes) scanRoot(n);
}

function scheduleScan(node: Element): void {
  pending.push(node);
  if (mutationTimer === undefined) {
    mutationTimer = window.setTimeout(flushMutations, 50);
  }
}

let observer: MutationObserver | null = null;

export function initEnhancers(): void {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initEnhancers(), { once: true });
    return;
  }
  scanRoot(document);

  if (observer || typeof MutationObserver === 'undefined') return;
  observer = new MutationObserver((mutations) => {
    for (const m of mutations) {
      for (const n of Array.from(m.addedNodes)) {
        if (n instanceof Element) scheduleScan(n);
      }
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });
}
